import React, { useEffect, useMemo, useState } from 'react';
import FormInput from '../../common/FormInput/FormInput';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import { formatCurrency } from '../../../utils';
import type { PaymentHistory } from '../../../types/payment';
import ModalSummary from './ModalSummary';
import styles from './CreateSubscriptionModal.module.css';

interface ConfirmPaymentModalProps {
  open: boolean;
  loading: boolean;
  payment: PaymentHistory | null;
  onConfirm: (paidAtIso: string) => void;
  onCancel: () => void;
}

const formatDateTimeLocalValue = (date: Date): string => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const ConfirmPaymentModal: React.FC<ConfirmPaymentModalProps> = ({
  open,
  loading,
  payment,
  onConfirm,
  onCancel,
}) => {
  const defaultPaidAtLocal = useMemo(() => formatDateTimeLocalValue(new Date()), [open]);
  const [paidAtLocal, setPaidAtLocal] = useState(defaultPaidAtLocal);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setPaidAtLocal(formatDateTimeLocalValue(new Date()));
      setError(null);
      return;
    }

    setPaidAtLocal(formatDateTimeLocalValue(new Date()));
    setError(null);
  }, [open]);

  useEscapeToClose(onCancel, open);

  if (!open || !payment) {
    return null;
  }

  const handleConfirm = () => {
    const trimmed = paidAtLocal.trim();
    if (!trimmed) {
      setError('Informe a data e hora do pagamento.');
      return;
    }

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      setError('Data e hora de pagamento inválidas.');
      return;
    }

    onConfirm(parsed.toISOString());
  };

  return (
    <div className={styles.overlay} role="presentation" onClick={onCancel}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-payment-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="confirm-payment-title" className={styles.title}>Confirmar pagamento</h2>
        <p className={styles.description}>
          A cobrança será marcada como <strong>paga</strong> no portal. O gateway não é alterado automaticamente.
        </p>

        <ModalSummary
          items={[
            { label: 'Valor', value: formatCurrency(payment.value, payment.currency) },
            { label: 'Vencimento', value: payment.due_date ?? '—' },
          ]}
        />

        <FormInput
          id="confirm-payment-paid-at"
          label="Data e hora do pagamento"
          type="datetime-local"
          value={paidAtLocal}
          onChange={(event) => {
            setPaidAtLocal(event.target.value);
            setError(null);
          }}
          error={error ?? undefined}
          required
        />

        <div className={styles.actions}>
          <button type="button" className={styles.cancelButton} onClick={onCancel} disabled={loading}>
            Cancelar
          </button>
          <button type="button" className={styles.confirmButton} onClick={handleConfirm} disabled={loading}>
            {loading ? 'Salvando...' : 'Confirmar pagamento'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmPaymentModal;
